/**
 * 推薦點區塊——四個分類頁籤，每個分類最多 5 張卡片。
 *
 * 一個景點滿載是 20 筆推薦點 × 9 個欄位 × 3 語系。分類頁籤把它切成一次只看 5
 * 張卡片，語系則沿用整頁共用的語言頁籤——兩者是正交的維度，混在同一排會變成
 * 12 個頁籤而看不出哪個是哪個。
 *
 * 頁籤上的 `4/5` 是該分類已填的筆數：船上的清單頁固定五格，缺的那格會空著，
 * 所以「還差幾筆」是發布前真的需要知道的事。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { SpotPoiCard } from './SpotPoiCard';
import {
  deleteSpotPoi,
  listSpotPois,
  POI_CATEGORIES,
  POI_CATEGORY_LABEL,
  POI_MAX_SEQ,
  reorderSpotPois,
  upsertSpotPoi,
  type PoiCategory,
  type SpotPoi,
  type SpotPoiPatch,
} from '../lib/api-client';

interface Props {
  spotId: string;
  /** CMS 語系代碼（zh-TW / en / ja）。 */
  locale: string;
  localeLabel: string;
  /** 景點的窗屏編號；往下傳給卡片，讓挑選素材限定在這個景點的資料夾。 */
  roiId?: string;
}

export function SpotPoiFields({ spotId, locale, localeLabel, roiId }: Props): JSX.Element {
  const qc = useQueryClient();
  const [category, setCategory] = useState<PoiCategory>('food');

  const query = useQuery({
    queryKey: ['spot.pois', spotId],
    queryFn: () => listSpotPois(spotId),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['spot.pois', spotId] });
  };

  const patchMutation = useMutation({
    mutationFn: (v: { seq: number; patch: SpotPoiPatch }) =>
      upsertSpotPoi(spotId, category, v.seq, v.patch),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (seq: number) => deleteSpotPoi(spotId, category, seq),
    onSuccess: invalidate,
  });

  const reorderMutation = useMutation({
    mutationFn: (order: string[]) => reorderSpotPois(spotId, category, order),
    onSuccess: invalidate,
  });

  const list: SpotPoi[] = query.data?.categories?.[category] ?? [];
  const counts = query.data?.counts;
  const saving = patchMutation.isPending || removeMutation.isPending || reorderMutation.isPending;

  /**
   * 新增一筆。
   *
   * 序號取「第一個沒被佔用的格子」而非 `length + 1`：刪掉 #2 之後清單是
   * [#1, #3]，用長度會算出 #3 而撞到既有那筆。
   */
  const addOne = (): void => {
    const used = new Set(list.map((p) => p.seq));
    for (let seq = 1; seq <= POI_MAX_SEQ; seq += 1) {
      if (!used.has(seq)) {
        patchMutation.mutate({ seq, patch: { name_i18n: {} } });
        return;
      }
    }
  };

  const move = (index: number, delta: number): void => {
    const to = index + delta;
    if (to < 0 || to >= list.length) return;
    const order = list.map((p) => p.id);
    const [moved] = order.splice(index, 1);
    order.splice(to, 0, moved as string);
    reorderMutation.mutate(order);
  };

  const error = query.error ?? patchMutation.error ?? removeMutation.error ?? reorderMutation.error;

  return (
    <fieldset
      style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, margin: '12px 0' }}
    >
      <legend style={{ fontSize: 13, fontWeight: 600 }}>推薦點（{localeLabel}）</legend>

      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
        窗屏第三、四層畫面的附近店家。每個分類最多 {POI_MAX_SEQ} 筆，順序即畫面上的排列順序。
        <strong>此區塊即時儲存</strong>，不需按下方的「儲存變更」。
      </p>

      <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
        {POI_CATEGORIES.map((c) => {
          const active = c === category;
          const n = counts?.[c] ?? 0;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              style={{
                padding: '6px 12px',
                border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: 4,
                background: active ? '#eff6ff' : '#fff',
                color: active ? '#2563eb' : '#6b7280',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              {POI_CATEGORY_LABEL[c]}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  color: n === POI_MAX_SEQ ? '#10b981' : '#9ca3af',
                }}
              >
                {n}/{POI_MAX_SEQ}
              </span>
            </button>
          );
        })}
      </div>

      {query.isLoading && <div style={{ fontSize: 13 }}>載入中…</div>}
      {error && <div className="error-banner">{(error as Error).message}</div>}

      {!query.isLoading && list.length === 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: '8px 0' }}>
          這個分類還沒有推薦點。可以按下方新增，或用上方的「從媒體庫資料夾套用」
          一次帶入整個景點的內容。
        </p>
      )}

      {list.map((poi, i) => (
        <SpotPoiCard
          key={poi.id}
          poi={poi}
          locale={locale}
          localeLabel={localeLabel}
          {...(roiId === undefined ? {} : { roiId })}
          index={i}
          total={list.length}
          saving={saving}
          onPatch={(patch) => patchMutation.mutate({ seq: poi.seq, patch })}
          onMove={(delta) => move(i, delta)}
          onRemove={() => {
            if (
              window.confirm(`確定刪除 ${POI_CATEGORY_LABEL[category]} 第 ${poi.seq} 筆推薦點？`)
            ) {
              removeMutation.mutate(poi.seq);
            }
          }}
        />
      ))}

      {list.length < POI_MAX_SEQ && (
        <Button variant="secondary" onClick={addOne} disabled={saving}>
          ＋ 新增推薦點
        </Button>
      )}
    </fieldset>
  );
}
